import {responses} from "@/services/responses";

export const errorHandler = {
    /**
     * Operator-facing message for a request that reached no server route.
     *
     * @returns {string} The 404 message shown in the error dialog.
     */
    throwNotFound() {
        return 'Failed to Connect to Server (Url Not Found)!';
    },

    /**
     * Operator-facing message for a request that exceeded the client timeout.
     *
     * @returns {string} The timeout message shown in the error dialog.
     */
    throwTimeOut() {
        return 'Connection Time Out!';
    },

    /**
     * Unwrap a failed request into a human-readable message, surface it to the
     * operator, and re-throw it.
     *
     * Contract (TicketApp #29691 - SPEC §4.3 and §7.3):
     *
     * 1. UNWRAP ORDER - the body is unwrapped in two passes, in this order:
     *    - Pass 1 reads the HTTP body (`err.response.data`). It takes the first
     *      key it recognises: `.error`, `.Error`, `.response_message`,
     *      `.message`, `.Print` (only when the Print text contains "failed"),
     *      then `.Message`.
     *    - Pass 2 runs ONLY when pass 1 left an OBJECT behind. It reads the
     *      body's `.response` envelope and takes the same keys from it. The
     *      `.Message` value is captured BEFORE that read, because the read
     *      replaces the original object - without the capture, a
     *      `{"Message": "..."}` body would already be `undefined` by the time
     *      the `.Message` branch could see it (the S2 trap).
     *    A plain-string body - the shape the backend returns since the D3
     *    error-body change - passes both passes untouched, because every
     *    branch is guarded by a `typeof ... === 'object'` test.
     *
     * 2. DIALOG OWNERSHIP - `isSweetalert` (default `false`) is the "the
     *    caller owns the terminal dialog" flag:
     *    - `false`: this handler opens the shared dialog through
     *      `responses.throwErr` and leaves it visible.
     *    - `true`: this handler opens NO dialog; the caller renders its own
     *      terminal state, and a dialog the caller already has open stays
     *      visible and untouched.
     *
     * 3. NO SELF-CLOSE - the dialog this handler opens is never closed by this
     *    handler. Stale-modal suppression is preserved by SweetAlert2 itself:
     *    `Swal.fire` destroys the modal that is already open and puts the new
     *    one in its place (sweetalert2 11.18.0,
     *    `node_modules/sweetalert2/dist/sweetalert2.js:4316-4331`).
     *
     * 4. RE-THROW - the error is always re-thrown, so every call site's
     *    `.catch` still fires. The re-thrown `Error` carries the SAME
     *    unwrapped message that was rendered, so a caller reading
     *    `err.message` sees the operator-facing text.
     *
     * @param {Error|object} err - The rejected value handed over by the HTTP client.
     * @param {boolean} [isSweetalert=false] - Skip the shared dialog; the caller owns the terminal state.
     * @param {string} [title] - Optional title for the shared dialog.
     * @returns {void} Never returns - the method always throws.
     * @throws {Error} Carries the unwrapped, human-readable message.
     */
    tomcatError(err, isSweetalert = false, title = undefined,) {
        let response = err.response;
        let errorMessage;
        if (response) {
            console.error(response)
            if (response.status === 404) {
                errorMessage = this.throwNotFound();
            } else {
                errorMessage = response.data;
                // `typeof null === 'object'`, so a body that is null would throw
                // on the first property read below. Normalising it to an empty
                // object keeps every condition safely falsy and lets the
                // readability guard further down produce a readable message.
                if (errorMessage === null || errorMessage === undefined) {
                    errorMessage = {};
                }
                if (errorMessage.error && typeof errorMessage === 'object') {
                    errorMessage = errorMessage.error;
                } else if (errorMessage.Error && typeof errorMessage === 'object') {
                    errorMessage = errorMessage.Error;
                } else if (errorMessage.response_message && typeof errorMessage === 'object') {
                    errorMessage = errorMessage.response_message;
                } else if (errorMessage.message && typeof errorMessage === 'object') {
                    errorMessage = errorMessage.message;
                } else if (errorMessage.Print && errorMessage.Print.toString().includes('failed')) {
                    errorMessage = errorMessage.Print;
                } else if (errorMessage.Message && typeof errorMessage === 'object') {
                    // The backend's pre-fix error body shape (`{"Message": "..."}`).
                    // It is taken here so the flat body never survives pass 1 as
                    // an object; pass 2 captures the same key again as a safety
                    // net for a body that only carries it inside a `.response`
                    // envelope.
                    errorMessage = errorMessage.Message;
                }
            }
        } else if (err.code === `ECONNABORTED`) {
            errorMessage = this.throwTimeOut()
        } else {
            console.error(err)
            errorMessage = err.message;
        }
        if (errorMessage === `Network Error`) {
            title = title ? title : errorMessage;
            errorMessage = `Please Check your Internet`;
        }
        if (typeof errorMessage === 'object') {
            // S2 TRAP - capture `.Message` BEFORE the envelope read below. That
            // read replaces the whole object, so for a `{"Message": "..."}` body
            // it would leave `undefined` here and every branch underneath would
            // throw a TypeError before the message could ever be extracted.
            const capturedMessage = (errorMessage !== null && typeof errorMessage.Message === 'string')
                ? errorMessage.Message
                : undefined;
            // `typeof null === 'object'`, so a null body would throw on the
            // envelope read below.
            if (errorMessage === null) {
                errorMessage = {};
            }
            errorMessage = errorMessage.response;
            // Null-guard for the same reason: most bodies carry no `.response`
            // envelope, so the read above yields `undefined` and the branch
            // chain would dereference it. An empty object keeps every condition
            // safely falsy.
            if (errorMessage === undefined || errorMessage === null) {
                errorMessage = {};
            }
            if (errorMessage.error && typeof errorMessage === 'object') {
                errorMessage = errorMessage.error;
            } else if (errorMessage.Error && typeof errorMessage === 'object') {
                errorMessage = errorMessage.Error;
            } else if (errorMessage.response_message && typeof errorMessage === 'object') {
                errorMessage = errorMessage.response_message;
            } else if (errorMessage.message && typeof errorMessage === 'object') {
                errorMessage = errorMessage.message;
            }else if (errorMessage.Message && typeof errorMessage === 'object') {
                errorMessage = errorMessage.Message;
            } else if (errorMessage.Print && errorMessage.Print.toString().includes('failed')) {
                errorMessage = errorMessage.Print;
            } else if (capturedMessage !== undefined) {
                // The string `.Message` captured before the envelope read above
                // - the backend's `{"Message": "..."}` body shape.
                errorMessage = capturedMessage;
            }
        }
        // READABILITY GUARD - every branch above can still leave a value that is
        // not text: an object whose keys none of them matched, or an empty body.
        // Such a value is replaced by a plain sentence, so the operator never
        // reads "[object Object]" and never faces a blank modal (SPEC §7.3).
        // The full body is still available in the console log above.
        if (errorMessage === undefined || errorMessage === null || errorMessage === ''
            || typeof errorMessage === 'object') {
            errorMessage = `An unexpected error occurred. Please try again.`;
        }
        if (!isSweetalert) {
            // The shared dialog. `responses.throwErr` calls `Swal.fire`, which
            // destroys the modal that is already open and replaces it with this
            // one - that is the stale-modal suppression the removed force-close
            // used to provide, done by the library instead of by a close that
            // also closed the dialog it had just opened.
            responses.throwErr(errorMessage, title);
        }
        // NO FORCE-CLOSE - the dialog opened above is the operator's terminal
        // feedback and stays visible; closing it in the same tick is the
        // open-then-close race that turned a backend failure into silence
        // (SPEC §7.3). When `isSweetalert` is true no dialog was opened here,
        // so the caller's own dialog stays visible and untouched.
        throw new Error(errorMessage);
    },

}